import { Button, DialogV2, Loader, Pill, Typography } from '@recruit-robin/robin-theme/web-components';
import { Bell_01, Link_03, LinkBroken } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useMemo, useState } from 'react';

import type { default as Candidate } from 'model/Candidate/Candidate';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useSendCallMobilePushNotification } from 'shared/legacy/hooks/useSendCallMobilePushNotification';
import { useStyles } from './MobileAppConnectedPill.styles';

export const MobileAppConnectedPill = ({ candidate }: { candidate: Candidate }) => {
  const { dictionary } = useContext(LocalizationContext);
  const classes = useStyles();

  const [isVisible, setIsVisible] = useState(false);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const currentUserQuery = useCurrentUserQuery();
  const isMobileAppConnected = !!currentUserQuery.data?.expo_push_token;

  const sendCallMobilePushNotification = useSendCallMobilePushNotification();

  const sendNotification = () => {
    if (isSendingNotification || !isMobileAppConnected || !candidate.not_obfuscated_phone) return;

    setIsSendingNotification(true);

    sendCallMobilePushNotification({
      candidateName: candidate.name,
      candidatePhoneNumber: candidate.not_obfuscated_phone,
    }).finally(() => {
      setIsSendingNotification(false);
    });
  };

  const pill = useMemo(() => {
    if (isMobileAppConnected) {
      return (
        <Pill
          label={dictionary.checkYourPhone}
          startIcon={isSendingNotification ? <Loader size={16} color={(c) => c.secondary.emerald} /> : Bell_01}
          style="icon-removable"
          type="success-light"
          onClick={sendNotification}
        />
      );
    }

    return (
      <Pill
        label={dictionary.phoneNotConnected}
        startIcon={LinkBroken}
        style="icon-removable"
        type="error-light"
        onClick={() => setIsVisible(true)}
      />
    );
  }, [dictionary, isMobileAppConnected, isSendingNotification, sendNotification]);

  return (
    <>
      {pill}

      <DialogV2.Root customCss={classes.dialogRoot} isVisible={isVisible}>
        <DialogV2.Backdrop />

        <DialogV2.Content customCss={classes.dialogContent}>
          <DialogV2.Header customCss={classes.dialogHeader}>
            <div css={classes.linkBadge}>
              <Link_03 size={24} color={(c) => c.secondary.emerald[50]} />
            </div>
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {dictionary.connectYourMobileToRobin}
            </Typography>
          </DialogV2.Header>

          <DialogV2.Body customCss={classes.dialogBody}>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {dictionary.connectYourMobileToRobinDescription}
            </Typography>
          </DialogV2.Body>

          <DialogV2.Footer customCss={classes.dialogFooter}>
            <Button
              customCss={classes.button}
              style="tonal"
              type="highlight"
              label={dictionary.gotIt}
              onClick={() => setIsVisible(false)}
              contained
            />
          </DialogV2.Footer>
        </DialogV2.Content>
      </DialogV2.Root>
    </>
  );
};
